import { useState, useEffect, useCallback, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';

// Consistent breakpoints matching your design system
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1200,
  desktop: 1280,
  wide: 1440
};

// Hysteresis thresholds to prevent jumping at boundaries
const HYSTERESIS_BREAKPOINTS = {
  mobileToTablet: 768,
  tabletToMobile: 750,
  tabletToDesktop: 1024,
  desktopToTablet: 1000,
  desktopToWide: 1280,
  wideToDesktop: 1260
};

export const useResponsive = () => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  const [currentBreakpoint, setCurrentBreakpoint] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    return width < BREAKPOINTS.mobile ? 'mobile' :
           width < BREAKPOINTS.tablet ? 'tablet' :
           width < BREAKPOINTS.wide ? 'desktop' : 'wide';
  });
  
  const timeoutRef = useRef(null);
  
  const updateDimensions = useCallback(() => {
    // Debounce resize updates to prevent jumpiness
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      // Update dimensions immediately
      setDimensions({
        width: newWidth,
        height: newHeight
      });
      
      // Update breakpoint with hysteresis to prevent oscillation
      setCurrentBreakpoint(prev => {
        if (prev === 'mobile' && newWidth >= HYSTERESIS_BREAKPOINTS.mobileToTablet) {
          return 'tablet';
        }
        if (prev === 'tablet' && newWidth < HYSTERESIS_BREAKPOINTS.tabletToMobile) {
          return 'mobile';
        }
        if (prev === 'tablet' && newWidth >= HYSTERESIS_BREAKPOINTS.tabletToDesktop) {
          return 'desktop';
        }
        if (prev === 'desktop' && newWidth < HYSTERESIS_BREAKPOINTS.desktopToTablet) {
          return 'tablet';
        }
        if (prev === 'desktop' && newWidth >= HYSTERESIS_BREAKPOINTS.desktopToWide) {
          return 'wide';
        }
        if (prev === 'wide' && newWidth < HYSTERESIS_BREAKPOINTS.wideToDesktop) {
          return 'desktop';
        }
        return prev; // No change if within hysteresis band
      });
    }, 16); // ~60fps
  }, []);
  
  useEffect(() => {
    // Set initial dimensions immediately (no debounce for initial load)
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    });
    
    window.addEventListener('resize', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [updateDimensions]);
  
  const { width } = dimensions;
  
  return {
    width,
    height: dimensions.height,
    isMobile: currentBreakpoint === 'mobile',
    isTablet: currentBreakpoint === 'tablet',
    isDesktop: currentBreakpoint === 'desktop',
    isWide: currentBreakpoint === 'wide',
    breakpoint: currentBreakpoint
  };
};

export const useRoleAwareLayout = () => {
  const { user, currentUser } = useFamilyContext();
  const responsive = useResponsive();
  const [layoutReady, setLayoutReady] = useState(false);
  
  // Determine the effective user role (considering parent viewing kid dashboard)
  let effectiveRole;
  
  // If currentUser is explicitly set, use its role
  if (currentUser) {
    if (currentUser.isParent === true || currentUser.originalParent === true) {
      effectiveRole = 'parent';
    } else {
      effectiveRole = 'kid';
    }
  } else if (user) {
    // If no currentUser but we have a user, determine from user data
    if (user.role === 'parent' || user.isParent === true) {
      effectiveRole = 'parent';
    } else {
      effectiveRole = 'kid';
    }
  } else {
    effectiveRole = 'guest';
  }
  
  const isActualParent = user?.role === 'parent' || user?.isParent === true;
  

  // Mark layout as ready after initial render to prevent flash
  useEffect(() => {
    const timer = setTimeout(() => setLayoutReady(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  return {
    ...responsive,
    isParent: effectiveRole === 'parent',
    isKid: effectiveRole === 'kid',
    isActualParent,
    layoutMode: responsive.isMobile ? 'mobile' : 'desktop',
    userRole: effectiveRole,
    currentUser,
    layoutReady,
    // Helper combinations
    isMobileParent: responsive.isMobile && effectiveRole === 'parent',
    isMobileKid: responsive.isMobile && effectiveRole === 'kid',
    isDesktopParent: !responsive.isMobile && effectiveRole === 'parent',
    isDesktopKid: !responsive.isMobile && effectiveRole === 'kid'
  };
};